import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Dialog, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { downloadNpiCsv } from "@services/facilityNetworkLink";
import cloudUploadIcon from "@assets/facilityNetwork/ba-cloud-upload.png";
import documentIcon from "@assets/facilityNetwork/ba-document.svg";
import dismissIcon from "@assets/facilityNetwork/ba-dismiss.svg";
import fileDismissIcon from "@assets/facilityNetwork/ba-file-dismiss.svg";
import reviewChevronIcon from "@assets/facilityNetwork/ba-review-chevron.svg";
import reviewChevronDisabledIcon from "@assets/facilityNetwork/ba-review-chevron-disabled.svg";
import FacilityNetworkFigmaIcon from "./FacilityNetworkFigmaIcon";
import {
  CHILD_NPI_CSV_TEMPLATE_FILENAME,
  formatCsvFileSize,
  isCsvUploadFile,
} from "./bulkAddCsvUtils";
import { FN } from "./facilityNetworkTheme";

type Props = {
  open: boolean;
  reviewing?: boolean;
  onClose: () => void;
  onReview: (file: File) => Promise<boolean> | boolean;
};

const DROP_BG = "#f3fdfd";
const DROP_BORDER = "#3b9bb1";
const FILE_ROW_BORDER = "#c8c6c4";
const CANCEL_BORDER = "#d4d4d4";
const MUTED_LABEL = "#6b7280";
const DIALOG_SHADOW =
  "0px 8px 16px 0px rgba(0,0,0,0.14), 0px 0px 2px 0px rgba(0,0,0,0.12)";
const REVIEW_SHADOW = "0px 2px 4px rgba(0,0,0,0.25)";
const REVIEW_GRADIENT = "linear-gradient(90deg, #0a829d 0%, #58c2d8 100%)";
const REVIEW_HOVER = "linear-gradient(90deg, #065b70 0%, #0a829d 100%)";

const csvInputAccept = ".csv,text/csv,application/csv,text/comma-separated-values";

function downloadTemplate(): void {
  downloadNpiCsv(CHILD_NPI_CSV_TEMPLATE_FILENAME, ["npi"], []);
}

export default function BulkAddCsvDialog({
  open,
  reviewing = false,
  onClose,
  onReview,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setDragging(false);
    }
  }, [open]);

  const openPicker = () => {
    if (reviewing) return;
    fileInputRef.current?.click();
  };

  const applyFile = (next: File | undefined) => {
    if (!next) return;
    if (!isCsvUploadFile(next)) {
      toast.error("Upload a CSV file.");
      return;
    }
    setFile(next);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.files?.[0];
    event.target.value = "";
    applyFile(next);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragging(false);
    if (reviewing) return;
    applyFile(event.dataTransfer.files?.[0]);
  };

  const handleClose = () => {
    if (reviewing) return;
    onClose();
  };

  const handleReview = async () => {
    if (!file || reviewing) return;
    const ok = await onReview(file);
    if (ok) onClose();
  };

  const hasFile = file != null;

  return (
    <Dialog
      open={open}
      onClose={reviewing ? undefined : handleClose}
      maxWidth={false}
      transitionDuration={0}
      aria-labelledby="bulk-add-title"
      PaperProps={{
        sx: {
          width: 600,
          maxWidth: "calc(100vw - 32px)",
          m: 2,
          borderRadius: "4px",
          bgcolor: "#fff",
          boxShadow: DIALOG_SHADOW,
          boxSizing: "border-box",
          overflow: "visible",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pt: 1.5,
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pl: 3,
            pr: 3,
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          <Typography
            id="bulk-add-title"
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 14,
              fontWeight: 600,
              lineHeight: "20px",
              color: "#242424",
            }}
          >
            Bulk add
          </Typography>
          <Box
            component="button"
            type="button"
            aria-label="Close"
            onClick={handleClose}
            disabled={reviewing}
            sx={{
              width: 28,
              height: 24,
              p: "2px",
              border: "none",
              bgcolor: "transparent",
              cursor: reviewing ? "default" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FacilityNetworkFigmaIcon src={dismissIcon} size={20} />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: hasFile ? "9px" : 1,
            px: 3,
            py: 2.5,
            borderTop: "1px solid #e5e5e5",
            borderBottom: "1px solid #e5e5e5",
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 14,
              fontWeight: 600,
              lineHeight: "20px",
              color: "#242424",
            }}
          >
            Upload CSV
          </Typography>

          <Box
            data-testid="bulk-add-dropzone"
            onClick={(event) => {
              if ((event.target as HTMLElement).closest("button")) return;
              if (!hasFile) openPicker();
            }}
            onDragOver={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (!reviewing) setDragging(true);
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setDragging(false);
            }}
            onDrop={handleDrop}
            sx={{
              alignSelf: "stretch",
              maxWidth: "100%",
              boxSizing: "border-box",
              bgcolor: dragging ? "#e8fbfb" : DROP_BG,
              border: `1.5px dashed ${DROP_BORDER}`,
              borderRadius: "8px",
              cursor: hasFile ? "default" : "pointer",
              overflow: "visible",
            }}
          >
            {hasFile && file ? (
              <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "center" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    width: "100%",
                    bgcolor: "#fff",
                    border: `1px solid ${FILE_ROW_BORDER}`,
                    borderRadius: "4px",
                    p: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                    <FacilityNetworkFigmaIcon src={documentIcon} size={20} />
                    <Typography
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontFamily: FN.fontFamily,
                        fontSize: 14,
                        fontWeight: 600,
                        lineHeight: "20px",
                        color: FN.text,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {file.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: FN.fontFamily,
                        fontSize: 14,
                        lineHeight: "20px",
                        color: FN.muted,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      {formatCsvFileSize(file.size)}
                    </Typography>
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    disabled={reviewing}
                    onClick={() => setFile(null)}
                    sx={{
                      width: 12,
                      height: 12,
                      p: 0,
                      border: "none",
                      bgcolor: "transparent",
                      cursor: reviewing ? "default" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <FacilityNetworkFigmaIcon src={fileDismissIcon} size={12} />
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 4,
                  px: 2,
                  gap: 1,
                }}
              >
                <FacilityNetworkFigmaIcon src={cloudUploadIcon} width={46} height={40} />
                <Typography
                  sx={{
                    fontFamily: FN.fontFamily,
                    fontSize: 16,
                    fontWeight: 600,
                    lineHeight: 1.474,
                    color: MUTED_LABEL,
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  Drop or click to upload
                </Typography>
                <Button
                  type="button"
                  onClick={openPicker}
                  disabled={reviewing}
                  sx={{
                    textTransform: "none",
                    minHeight: 32,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: "4px",
                    bgcolor: FN.primary,
                    color: "#fff",
                    fontFamily: FN.fontFamily,
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: "20px",
                    boxShadow: "none",
                    "&:hover": { bgcolor: FN.pressed, boxShadow: "none" },
                  }}
                >
                  Browse files
                </Button>
                <Button
                  type="button"
                  onClick={downloadTemplate}
                  disabled={reviewing}
                  sx={{
                    textTransform: "none",
                    minHeight: 32,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: "4px",
                    bgcolor: "transparent",
                    color: FN.text,
                    fontFamily: FN.fontFamily,
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: "20px",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "transparent", boxShadow: "none" },
                  }}
                >
                  Download Template
                </Button>
              </Box>
            )}
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            accept={csvInputAccept}
            hidden
            aria-label="CSV file"
            onChange={handleInputChange}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 3,
            pt: 1,
            pb: 3,
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          <Button
            type="button"
            onClick={handleClose}
            disabled={reviewing}
            sx={{
              textTransform: "none",
              minHeight: 32,
              height: 32,
              px: 1.5,
              py: 0.75,
              borderRadius: "4px",
              border: `1px solid ${CANCEL_BORDER}`,
              bgcolor: "#fff",
              color: FN.text,
              fontFamily: FN.fontFamily,
              fontSize: 14,
              fontWeight: 600,
              lineHeight: "20px",
              boxShadow: "none",
              "&:hover": { bgcolor: "#fafafa", border: `1px solid ${CANCEL_BORDER}`, boxShadow: "none" },
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            disabled={!hasFile || reviewing}
            onClick={() => {
              void handleReview();
            }}
            endIcon={
              reviewing ? (
                <CircularProgress size={12} sx={{ color: "#fff" }} />
              ) : (
                <FacilityNetworkFigmaIcon
                  src={hasFile ? reviewChevronIcon : reviewChevronDisabledIcon}
                  size={12}
                />
              )
            }
            sx={{
              textTransform: "none",
              minHeight: 32,
              height: 32,
              px: 2,
              py: 1,
              borderRadius: "4px",
              fontFamily: FN.fontFamily,
              fontSize: 14,
              fontWeight: 600,
              lineHeight: "20px",
              boxShadow: REVIEW_SHADOW,
              "& .MuiButton-endIcon": { ml: 1, mr: 0 },
              background: REVIEW_GRADIENT,
              color: "#fff",
              "&:hover": { background: REVIEW_HOVER, boxShadow: REVIEW_SHADOW },
              "&.Mui-disabled": {
                background: "#f5f5f5",
                color: "#d4d4d4",
                boxShadow: REVIEW_SHADOW,
              },
            }}
          >
            Review
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
